import { browserCookieUtils } from './cookies_utils';
import GustoAnalytics from './gusto_analytics';
import { isBrowser } from './helpers/environment_helper';
import * as Helper from './helpers/helper';
import { PageHelper } from './helpers/helper';
import { browserMetadataUtils } from './helpers/metadata_utils';
import * as WindowHelper from './helpers/window_helper';
import Refcodes from './refcodes';
import Session from './session';

function firePageEvent(
  gustoAnalyticsSingleton: GustoAnalytics,
  properties: any,
  extraProperties: any = {},
) {
  const pageHelper = new PageHelper(gustoAnalyticsSingleton.metadataUtils);
  let pageName = pageHelper.constructPageName();

  // If pageName is unknown, don't fire auto page event
  if (pageName !== '') {
    gustoAnalyticsSingleton.log('auto page event');

    // If framework/blog/ask-gusto/help center/zp, don't use full page name as event name
    // It'll create too many event names!!
    if (pageHelper.isCategorizedPage(pageName)) {
      properties.page_name = pageHelper.getPageSubName(pageName);
      pageName = pageHelper.getPageCategory(pageName) ?? '';
    }

    const friendName = pageHelper.getFriendName();

    if (friendName) {
      properties.page_name = friendName;
      pageName = pageHelper.INVITE_FRIENDS.NAME;
    }

    gustoAnalyticsSingleton.page({
      name: pageName,
      data: Helper.deepCopyMerge(properties, extraProperties, { auto: true }),
    });
  } else {
    console.error(
      `Unknown page name, not firing auto page event. Url: ${globalThis.location.href}`,
    );
  }
}

function autoPageEvent(gustoAnalyticsSingleton: GustoAnalytics, properties: any) {
  const { extraPagePropParser } = gustoAnalyticsSingleton.config;

  if (typeof extraPagePropParser === 'function') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        firePageEvent(gustoAnalyticsSingleton, properties, extraPagePropParser());
      });
    } else {
      firePageEvent(gustoAnalyticsSingleton, properties, extraPagePropParser());
    }
  } else {
    firePageEvent(gustoAnalyticsSingleton, properties);
  }
}

function sendVisibilityEvents() {
  if (document.visibilityState) {
    globalThis.GustoAnalytics.track({
      targetProvider: 'snowplow',
      eventCategory: 'Page',
      eventName: 'Visibility',
      data: {
        visibilityState: document.visibilityState,
      },
    });
  }
}

function trackSessionStart(
  gustoAnalyticsSingleton: GustoAnalytics,
  properties: any,
  session: Session,
) {
  // Track session start
  if (session.sessionStartNeeded()) {
    gustoAnalyticsSingleton.track({
      eventCategory: 'VisitorEvent',
      eventName: 'SessionStart',
      data: properties,
    });
    session.clearSessionStartNeeded();
  }
}

function init(
  gustoAnalyticsSingleton: GustoAnalytics,
  cookieUtils = browserCookieUtils,
  metadataUtils = browserMetadataUtils,
) {
  const session = new Session(cookieUtils);
  const refcodes = new Refcodes(cookieUtils, metadataUtils);
  const properties: Record<string, any> = metadataUtils.isPageVisible()
    ? refcodes.processReferralInformation()
    : {};

  if (isBrowser && !metadataUtils.isPageVisible()) {
    const handleVisibilityChange = () => {
      if (metadataUtils.isPageVisible()) {
        const referralInformation = refcodes.processReferralInformation();
        if (!refcodes.isDebugRefcodes(referralInformation)) {
          globalThis.GustoAnalytics.identify({ data: referralInformation });
        }
        document.removeEventListener(
          WindowHelper.pageVisibleEventName(),
          handleVisibilityChange,
          false,
        );
      }
    };
    document.addEventListener(WindowHelper.pageVisibleEventName(), handleVisibilityChange, false);
  }
  /*
   * Track page visibility change for time spend on page
   */
  if (isBrowser && gustoAnalyticsSingleton.visibilityEventsEnabled()) {
    document.addEventListener(WindowHelper.pageVisibleEventName(), sendVisibilityEvents, false);
  }
  gustoAnalyticsSingleton.analyticsReady(() => {
    // Identify user
    properties.has_signed_in = cookieUtils.getCookie('has_signed_in') === 'true';

    if (isBrowser) {
      trackSessionStart(gustoAnalyticsSingleton, properties, session);
    }

    if (gustoAnalyticsSingleton.config.autoIdentify !== false) {
      // Do not send debug refcodes as user properties
      if (refcodes.isDebugRefcodes(properties)) {
        gustoAnalyticsSingleton.identify({ data: { has_signed_in: properties.has_signed_in } });
      } else {
        gustoAnalyticsSingleton.identify({ data: properties });
      }
    }

    if (gustoAnalyticsSingleton.autoPageEvent()) {
      autoPageEvent(gustoAnalyticsSingleton, properties);
    }
    if (isBrowser && gustoAnalyticsSingleton.scrollEventsEnabled()) {
      document.addEventListener('scroll', () => {
        gustoAnalyticsSingleton.trackScrollEvent();
      });
    }

    // Drain preload event queue
    while ((globalThis.GustoAnalytics as any).length) {
      const methodCall = (globalThis.GustoAnalytics as any).shift();
      const methodName = methodCall[0];
      const args = Array.prototype.slice.call(methodCall, 1);

      (gustoAnalyticsSingleton as Record<string, any>)[methodName](...args);
    }

    globalThis.GustoAnalytics = gustoAnalyticsSingleton;
    globalThis.GustoAnalytics.initialize = !0;

    if (isBrowser) {
      const body = document.getElementsByTagName('body')[0];
      if (body) body.className += ' gusto-analytics-loaded';
    }
  });

  // set timeout to cause reload on session timeout
  session.setSessionTimeout();
}

export { autoPageEvent, firePageEvent, sendVisibilityEvents, trackSessionStart };
export default init;
